/**
 * The room's connection to the analysis worker.
 *
 * Why this is a module rather than a hook: one worker per tab, created lazily on the
 * first decision and kept warm for the session. Spinning one up per hand would pay the
 * module-parse cost on every deal, and the enumeration tables it builds are per-call
 * anyway.
 *
 * ## The fallback is not decoration
 *
 * `new Worker(new URL(...))` fails in three real situations: a browser without module
 * workers, a test runner with no `Worker` global, and a CSP that forbids worker-src.
 * In all three the room must still work — the analysis is the product — so it falls
 * back to running `decide` inline. The page will jank on split decisions in that mode
 * and it will be correct, which is the right way round.
 *
 * ## Stale replies are the caller's business
 *
 * Every request resolves, and every request is numbered. A player who acts quickly can
 * outrun a 400ms split analysis, and the room genuinely wants BOTH outcomes from that
 * race: the reply must not overwrite the panel for a position the player has already
 * left, and it must still arrive, because it is the grade for the decision they just
 * made. Dropping stale replies here would throw away the second one, so the id is
 * handed back and the room decides what a late answer means.
 */
import { decide, type Decision } from "../../../engine/blackjack/decide";
import { rehydrate, type TableSnapshot } from "../../../engine/blackjack/snapshot";
import { DOUBLE_DECK_S17 } from "../../../engine/blackjack/rules";
import type { AnalyseReply, AnalyseRequest } from "../workers/blackjack.worker";

export interface AnalysisResult {
  decision: Decision | null;
  /** How long it took, in ms. Shown on the page as a plain fact about the machine. */
  ms: number;
  /** True when the worker was unavailable and this ran on the interaction thread. */
  inline: boolean;
}

type Pending = { resolve: (r: AnalysisResult) => void; snap: TableSnapshot; timer: number };

/**
 * How long to wait for the worker before doing the work here instead.
 *
 * A backstop, not a performance target: the measured worst case is under half a
 * second, so anything past this is a worker that is not coming back. `onerror` covers
 * the failures the browser tells us about; a worker killed for memory pressure, or a
 * `postMessage` that throws inside the promise executor, tells us nothing at all — and
 * the room's study mode disables every button while an answer is outstanding, so a
 * request that never resolves is a locked screen with no way out.
 */
const WORKER_TIMEOUT_MS = 5000;

let worker: Worker | null = null;
/** Null until first attempted; false once construction has failed for good. */
let workerUsable: boolean | null = null;
let nextId = 1;
const pending = new Map<number, Pending>();

function ensureWorker(): Worker | null {
  if (workerUsable === false) return null;
  if (worker) return worker;
  try {
    worker = new Worker(new URL("../workers/blackjack.worker.ts", import.meta.url), {
      type: "module",
      name: "blackjack-analysis",
    });
    worker.onmessage = (e: MessageEvent<AnalyseReply>) => {
      const p = pending.get(e.data.id);
      if (!p) return;                                  // superseded, or already settled
      clearTimeout(p.timer);
      pending.delete(e.data.id);
      p.resolve({ decision: e.data.decision, ms: e.data.ms, inline: false });
    };
    /**
     * A worker that dies takes its outstanding requests with it.
     *
     * They are re-run HERE, on the interaction thread, rather than resolved with a
     * null — which is what this used to do while the comment claimed otherwise. The
     * difference matters because a null decision is not just a blank panel: the
     * caller that was waiting to GRADE a choice drops it, so a mistake made in the
     * moment the worker crashed silently improves the sitting's accuracy. That is the
     * one failure mode a room built on exactness must not have. The snapshot is kept
     * alongside each pending resolver for exactly this.
     */
    worker.onerror = () => {
      workerUsable = false;
      worker = null;
      const stranded = [...pending.values()];
      pending.clear();
      for (const p of stranded) { clearTimeout(p.timer); p.resolve(runInline(p.snap)); }
    };
    workerUsable = true;
    return worker;
  } catch {
    workerUsable = false;
    worker = null;
    return null;
  }
}

function runInline(snap: TableSnapshot): AnalysisResult {
  const t0 = Date.now();
  try {
    return { decision: decide(rehydrate(snap, DOUBLE_DECK_S17)), ms: Date.now() - t0, inline: true };
  } catch {
    return { decision: null, ms: Date.now() - t0, inline: true };
  }
}

/** Ask for a position to be analysed. Resolves once, whichever path answered. */
export function analyse(snap: TableSnapshot): { id: number; result: Promise<AnalysisResult> } {
  const id = nextId++;
  const w = ensureWorker();
  if (!w) return { id, result: Promise.resolve(runInline(snap)) };
  const req: AnalyseRequest = { id, snap };
  const result = new Promise<AnalysisResult>((resolve) => {
    const timer = setTimeout(() => {
      const p = pending.get(id);
      if (!p) return;
      pending.delete(id);
      p.resolve(runInline(snap));
    }, WORKER_TIMEOUT_MS) as unknown as number;
    pending.set(id, { resolve, snap, timer });
    try {
      w.postMessage(req);
    } catch {
      // A structured-clone failure or a dead port. Do it here rather than never.
      clearTimeout(timer);
      pending.delete(id);
      resolve(runInline(snap));
    }
  });
  return { id, result };
}

/** Test seam: forget the worker so the next call rebuilds it. */
export function resetAnalyst(): void {
  worker?.terminate();
  worker = null;
  workerUsable = null;
  pending.clear();
}
